import { BusinessFuel } from '../../business/models/business-fuel.model';
import { BusinessSummaryRental } from './business-summary-rental.model';
import { ReviewSummary } from '../../shared/models/review-summary.model';
import { Webcam } from './webcam.model';
import { BusinessTag } from '../../business/models/business-tag.model';


/**
 * Model for storing basic information about a business (such as the ID, name, city, and state). See Business class for
 * a more complete data model.
 */
export class BusinessSummary {
  /**
   * Unique identifier for this record in the database
   */
  id: string;

  /**
   * Business name
   */
  name: string;

  /**
   * True if the business is an FBO vs another business on the field.
   */
  isFbo: boolean;

  /**
   * Description of where this business is located on the airport. Typically, this will be a compass direction such as
   * "NW" (signifies it's on the Northwest side of the field), or "CTR" if it's in the center of the field.
   */
  locationOnField: string;

  /**
   * City where this business is located
   */
  city: string;

  /**
   * Abbreviation of the state where this business is located
   */
  state: string;

  /**
   * Primary contact phone number of the business
   */
  phone1: string;

  /**
   * ARINC frequency on which business may be contacted (if available)
   */
  airincFreq: string;

  /**
   * UNICOM frequency on which business may be contacted (if available)
   */
  unicomFreq: string;

  /**
   * Indicates availability of mantenance services at this business
   */
  maintenance: boolean;

  /**
   * Indicates availability of aircraft rental at this business
   */
  rental: BusinessSummaryRental;

  /**
   * Indicates availability of sales at this business
   */
  sales: boolean;

  /**
   * Indicates availability of flight training at this business
   */
  flightTraining: boolean;

  /**
   * Fuel available at business
   */
  fuel: BusinessFuel[];

  /**
   * True if the business has overview fee information available.
   */
  hasOverviewFees: boolean;

  /**
   * True if the business has non-overview fees.
   */
  hasFees: boolean;

  /**
   * Date of latest fee update if the business has reported fees
   */
  feesLastUpdate: Date;

  /**
   * Summary object of reviews for this entity
   */
  reviewSummary: ReviewSummary;

  /**
   * Summary object of reviews for this entity
   */
  webcams: Webcam[];

  /**
   * Indicates if the club should be hidden to public
   */
  hideBusiness: boolean;

  /**
   * List of the flight training awards for the business
   */
  fteAwardTags: BusinessTag[];

  /**
   * Indicates whether the business has been approved by AOPA
   */
  notApproved: boolean;

  feeTypes: string[];

  /**
   * Parses business summary
   * @param data {any} Business summary data as returned by API
   * @return {BusinessSummary} Parsed business summary
   */
  static parse(data: any): BusinessSummary {
    return Object.assign(new BusinessSummary(), {
      id: data.businessId,
      name: data.name,
      isFbo: data.isFbo,
      locationOnField: data.locationOnField,
      city: data.city,
      state: data.state,
      phone1: data.phone1,
      airincFreq: data.airincFreq ? data.airincFreq.toString() : null,
      unicomFreq: data.unicomFreq ? data.unicomFreq.toString() : null,
      maintenance: data.maintenance,
      rental: data.rental,
      sales: data.sales,
      flightTraining: data.flightTraining,
      fuel: (data.businessFuels || []).map(d => BusinessFuel.parse(d)),
      hasOverviewFees: data.hasOverviewFees,
      hasFees: data.hasFees,
      feesLastUpdate: data.feesLastUpdate,
      reviewSummary: data.reviewSummary,
      webcams: (data.webcams || []).map(d => Webcam.parse(d)),
      hideBusiness: data.hideBusiness,
      fteAwardTags: (data.fteAwardTags || []).map(d => BusinessTag.parse(d)),
      notApproved: data.notApproved,
      feeTypes: data.feeTypes || null
    });
  }
}
